import qs from "qs"
import { PopoverClose } from "@radix-ui/react-popover"
import { useCallback, useEffect, useState, useMemo, memo, Fragment } from "react"
import { LoaderCircleIcon, PencilIcon, PlusIcon, TrashIcon } from "lucide-react"
import { Outlet, useFetcher, useLoaderData, useRouteLoaderData } from "react-router"

import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { FiltersWrapper, type OnBeforeSubmit, type StoredSession } from "@/components/Filters"
import { hasPermission } from "@/machines/Authmachine"
import { getOrganisations, getProjectsByOrganisation } from "@/utils/projects"
import { fetchAPI, handleResponse, throwIfNotOk } from "@/utils"

import type { RootLoader } from "@/routes/root"
import type { VectorThresholdItem, DocumentClassificationType } from "@/utils/types"

const routeId = "vector-thresholds"
const sessionKey = "vector-thresholds-filter-storage"

const useVectorThresholdData = () => {
  const data = useRouteLoaderData<typeof loader>(routeId)

  return useMemo(() => {
    if (data == null) return null

    const classificationOptions = data.classifications.map(
      (classification: DocumentClassificationType) => classification.name,
    )

    return {
      ...data,
      classificationOptions,
      hasClassifications: data.classifications.length > 0,
    }
  }, [data])
}

async function loader() {
  try {
    const organisations = getOrganisations()
    if (organisations.length < 1) {
      return {
        loadedOrganisation: "",
        projects: [],
        loadedProject: "",
        classifications: [],
        selectedGroup: undefined,
        selectedClassification: undefined,
        activeUsage: "",
        activeUsageCriteria: "",
        thresholds: [],
        hasError: true,
      }
    }

    const sessionData = sessionStorage.getItem(sessionKey)
    const filters = sessionData != null ? (qs.parse(sessionData) as unknown as Partial<StoredSession>) : { raw: {} }

    const loadedOrganisation = filters.raw?.organisation ?? organisations[0]

    const projects = getProjectsByOrganisation(loadedOrganisation)

    const loadedProject =
      filters.raw?.project != null && projects.includes(filters.raw.project) ? filters.raw.project : (projects[0] ?? "")

    if (loadedProject.trim() === "") {
      return {
        loadedOrganisation,
        projects,
        loadedProject: "",
        classifications: [],
        selectedGroup: undefined,
        selectedClassification: undefined,
        activeUsage: "",
        activeUsageCriteria: "",
        thresholds: [],
        hasError: true,
      }
    }

    let classifications: DocumentClassificationType[] = []

    try {
      const classificationsResponse = await fetchAPI<DocumentClassificationType[]>(
        `/api/unityviewer/documentclassification/${loadedOrganisation}/${loadedProject}`,
      ).then(handleResponse)
      classifications = classificationsResponse
    } catch {
      classifications = []
    }

    const availableGroups = classifications.map((classification: DocumentClassificationType) => classification.name)

    const rawGroup = filters.raw?.group
    const selectedGroup =
      rawGroup != null && typeof rawGroup === "string" && rawGroup.trim() !== ""
        ? rawGroup === "default" || availableGroups.includes(rawGroup)
          ? rawGroup
          : undefined
        : undefined

    let selectedClassification: string | undefined = undefined

    const rawClassification = filters.raw?.classification
    if (
      rawClassification != null &&
      typeof rawClassification === "string" &&
      rawClassification.trim() !== "" &&
      selectedGroup != null
    ) {
      if (rawClassification === "default") {
        selectedClassification = "default"
      } else if (selectedGroup !== "default") {
        const groupClassifications =
          classifications
            .find((c: DocumentClassificationType) => c.name === selectedGroup)
            ?.subClassifications.map((sub) => sub.name) ?? []

        selectedClassification = groupClassifications.includes(rawClassification) ? rawClassification : undefined
      }
    }

    const activeUsage = filters.raw?.usage ?? ""
    const activeUsageCriteria = filters.raw?.usageCriteria ?? ""

    let thresholds: VectorThresholdItem[] = []
    let hasError = false

    try {
      const params = new URLSearchParams()

      if (activeUsage.trim() !== "") {
        params.set("Usage", activeUsage)

        const criteriaMapping: Record<string, string> = {
          "Starts With": "startsWith",
          "Ends With": "endsWith",
          Contains: "contains",
          Exact: "exact",
        }

        params.set("UsageCriteria", criteriaMapping[activeUsageCriteria] ?? "startsWith")
      }

      if (selectedGroup != null && selectedGroup !== "default") {
        params.set("Group", selectedGroup)
      }

      if (selectedClassification != null && selectedClassification !== "default") {
        params.set("Classification", selectedClassification)
      }

      const queryString = params.toString()
      const thresholdUrl = `/api/unityviewer/vectorthreshold/${loadedOrganisation}/${loadedProject}${queryString !== "" ? `?${queryString}` : ""}`

      const response = await fetchAPI<VectorThresholdItem[]>(thresholdUrl).then(handleResponse)
      thresholds = response ?? []
    } catch (error) {
      if (error instanceof Response && error.status !== 404) {
        hasError = true
      }

      thresholds = []
    }

    return {
      loadedOrganisation,
      projects,
      loadedProject,
      classifications,
      selectedGroup,
      selectedClassification,
      activeUsage,
      activeUsageCriteria,
      thresholds,
      hasError,
    }
  } catch {
    return {
      loadedOrganisation: "",
      projects: [],
      loadedProject: "",
      classifications: [],
      selectedGroup: undefined,
      selectedClassification: undefined,
      activeUsage: "",
      activeUsageCriteria: "",
      thresholds: [],
      hasError: true,
    }
  }
}

async function action({ request }: { request: Request }) {
  try {
    const formData = await request.formData()
    const {
      action,
      organisation,
      project,
      usage,
      classification,
      threshold,
      filterUsage,
      filterUsageCriteria,
      filterGroup,
      filterClassification,
    } = Object.fromEntries(formData) as Record<string, string>

    if (organisation == null || organisation.trim() === "") return { action, errorMessage: "Missing organisation" }
    if (project == null || project.trim() === "") return { action, errorMessage: "Missing project" }

    switch (action) {
      case "CREATE": {
        const finalClassification =
          classification == null || classification === "" || classification === "none" ? null : classification
        if (usage == null || usage.trim() === "") {
          return { action, errorMessage: "Usage is required" }
        }

        if (usage.trim().length > 20) {
          return { action, errorMessage: "Usage must not exceed 20 characters" }
        }

        if (threshold == null || threshold.trim() === "") {
          return { action, errorMessage: "Threshold is required" }
        }

        const thresholdValue = parseFloat(threshold)
        if (isNaN(thresholdValue) || thresholdValue <= 0 || thresholdValue > 1) {
          return { action, errorMessage: "Threshold must be greater than 0 and at most 1" }
        }

        try {
          await fetchAPI(`/api/unityviewer/vectorthreshold/${organisation}/${project}`, {
            method: "POST",
            body: {
              usage: usage.trim(),
              classification: finalClassification,
              threshold: Number(thresholdValue),
            },
          }).then(throwIfNotOk)
          return { action }
        } catch (error) {
          if (error instanceof Response) {
            try {
              const text = await error.text()
              const json = JSON.parse(text) as Record<string, unknown>
              const msg =
                typeof json.message === "string" ? json.message : typeof json.error === "string" ? json.error : null
              if (msg != null) return { action, errorMessage: msg }
            } catch {
              // fall through to generic message
            }
          }
          return { action, errorMessage: "Error creating threshold" }
        }
      }

      case "DELETE_ALL": {
        try {
          const queryParams: string[] = []

          if (filterUsage != null && filterUsage.trim() !== "") {
            queryParams.push(`Usage=${encodeURIComponent(filterUsage)}`)

            const criteriaMapping: Record<string, string> = {
              "Starts With": "startsWith",
              "Ends With": "endsWith",
              Contains: "contains",
              Exact: "exact",
            }
            const mappedCriteria = criteriaMapping[filterUsageCriteria] ?? "startsWith"
            queryParams.push(`UsageCriteria=${encodeURIComponent(mappedCriteria)}`)
          }

          if (filterGroup != null && filterGroup !== "") {
            if (filterGroup === "default") {
              queryParams.push(`Classification=`)
            } else {
              queryParams.push(`Group=${encodeURIComponent(filterGroup)}`)
              if (filterClassification != null && filterClassification !== "") {
                if (filterClassification === "default") {
                  queryParams.push(`Classification=`)
                } else {
                  queryParams.push(`Classification=${encodeURIComponent(filterClassification)}`)
                }
              }
            }
          } else if (
            filterClassification != null &&
            filterClassification !== "" &&
            filterClassification !== "default"
          ) {
            queryParams.push(`Classification=${encodeURIComponent(filterClassification)}`)
          }

          const queryString = queryParams.length > 0 ? `?${queryParams.join("&")}` : ""
          await fetchAPI(`/api/unityviewer/vectorthreshold/${organisation}/${project}${queryString}`, {
            method: "DELETE",
          }).then(throwIfNotOk)
          return { action, key: Math.random().toString(36) }
        } catch {
          return { action, errorMessage: "Error deleting all thresholds" }
        }
      }

      default:
        return { action, errorMessage: "Unknown action" }
    }
  } catch {
    return { action: "ERROR", errorMessage: "An unexpected error occurred" }
  }
}

async function mainAction({ request }: { request: Request }) {
  try {
    const formData = await request.formData()
    const {
      action,
      organisation,
      project,
      usage,
      classification,
      originalUsage,
      originalClassification,
      newUsage,
      newClassification,
      newThreshold,
      removeClassification,
    } = Object.fromEntries(formData) as Record<string, string>

    if (organisation == null || organisation.trim() === "") return { action, errorMessage: "Missing organisation" }
    if (project == null || project.trim() === "") return { action, errorMessage: "Missing project" }

    switch (action) {
      case "DELETE": {
        try {
          if (usage == null || usage.trim() === "") {
            return { action, errorMessage: "Missing usage for deletion" }
          }

          const deleteUrl =
            classification != null && classification.trim() !== ""
              ? `/api/unityviewer/vectorthreshold/${organisation}/${project}/${encodeURIComponent(usage)}/${encodeURIComponent(classification)}`
              : `/api/unityviewer/vectorthreshold/${organisation}/${project}/${encodeURIComponent(usage)}`

          await fetchAPI(deleteUrl, { method: "DELETE" }).then(throwIfNotOk)
          return { action }
        } catch {
          return { action, errorMessage: "Couldn't delete threshold" }
        }
      }

      case "EDIT": {
        try {
          if (originalUsage == null || originalUsage.trim() === "") {
            return { action, errorMessage: "Missing original usage" }
          }

          if (newUsage == null || newUsage.trim() === "" || newThreshold == null || newThreshold.trim() === "") {
            return { action, errorMessage: "Missing required fields" }
          }

          if (newUsage.trim().length > 20) {
            return { action, errorMessage: "Usage must not exceed 20 characters" }
          }

          const thresholdValue = parseFloat(newThreshold)
          if (isNaN(thresholdValue) || thresholdValue <= 0 || thresholdValue > 1) {
            return { action, errorMessage: "Threshold must be greater than 0 and at most 1" }
          }

          const editUrl =
            originalClassification != null && originalClassification.trim() !== ""
              ? `/api/unityviewer/vectorthreshold/${organisation}/${project}/${encodeURIComponent(originalUsage)}/${encodeURIComponent(originalClassification)}`
              : `/api/unityviewer/vectorthreshold/${organisation}/${project}/${encodeURIComponent(originalUsage)}`

          const body: {
            newUsage?: string
            removeClassification?: boolean
            newClassification?: string | null
            newThreshold: number
          } = {
            newThreshold: Number(thresholdValue),
          }

          if (newUsage.trim() !== originalUsage) {
            body.newUsage = newUsage.trim()
          }

          if (removeClassification === "true") {
            body.removeClassification = true
            body.newClassification = null
          } else {
            const originalClassificationValue = originalClassification ?? "none"
            if (newClassification !== originalClassificationValue) {
              body.newClassification = newClassification === "none" ? null : newClassification
            }
          }

          try {
            await fetchAPI(editUrl, { method: "PATCH", body }).then(throwIfNotOk)
            return { action }
          } catch (error) {
            if ((error as Response)?.status === 304) {
              return { action, errorMessage: "This entry already exists. Please edit that one instead." }
            }
            return { action, errorMessage: "Error updating threshold" }
          }
        } catch {
          return { action, errorMessage: "Error updating threshold" }
        }
      }

      default:
        return { action, errorMessage: "Unknown action" }
    }
  } catch {
    return { action: "ERROR", errorMessage: "An unexpected error occurred" }
  }
}

export function VectorThresholds() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useVectorThresholdData()

  const onBeforeSubmit = useCallback<OnBeforeSubmit>(
    (formData: FormData, reason: { type: "field"; name: string } | { type: "submitButton" }) => {
      if (reason.type === "field") {
        if (reason.name === "organisation") {
          formData.delete("project")
          formData.delete("group")
          formData.delete("classification")
        } else if (reason.name === "project") {
          formData.delete("group")
          formData.delete("classification")
        } else if (reason.name === "group") {
          formData.delete("classification")
        }
      }
    },
    [],
  )

  const filterOptions = useMemo(() => {
    if (data?.classifications == null) {
      return { groups: [], classificationsByGroup: {} }
    }

    const groups = data.classifications.map((classification: DocumentClassificationType) => classification.name)
    const classificationsByGroup = data.classifications.reduce(
      (acc: Record<string, string[]>, doc: DocumentClassificationType) => {
        acc[doc.name] = doc.subClassifications.map((sub) => sub.name)
        return acc
      },
      {} as Record<string, string[]>,
    )

    return { groups, classificationsByGroup }
  }, [data?.classifications])

  const currentClassifications = useMemo(() => {
    const currentGroup = data?.selectedGroup
    if (currentGroup == null || currentGroup === "" || currentGroup === "default") {
      return []
    }
    return filterOptions.classificationsByGroup[currentGroup] ?? []
  }, [data?.selectedGroup, filterOptions.classificationsByGroup])

  try {
    return (
      <Fragment>
        <h1 className="text-2xl font-bold tracking-tight lg:text-3xl">Vector Thresholds</h1>
        {rootData != null && data != null && (
          <div className="flex flex-wrap items-center gap-3">
            <FiltersWrapper submitOnChange canReset sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
              {rootData.organisations != null && rootData.organisations.length > 1 && (
                <FiltersWrapper.Select
                  name="organisation"
                  label="Organisation"
                  defaultValue={data.loadedOrganisation ?? ""}
                  items={rootData.organisations}
                />
              )}
              {data.projects != null && data.projects.length > 1 && (
                <FiltersWrapper.Select
                  name="project"
                  label="Project"
                  defaultValue={data.loadedProject ?? ""}
                  items={data.projects}
                />
              )}
              <FiltersWrapper.Input name="usage" label="Usage" maxLength={20} />
              {data.hasClassifications && (
                <>
                  <FiltersWrapper.Select
                    name="group"
                    label="Group"
                    defaultValue={data.selectedGroup ?? undefined}
                    items={[
                      { value: "default", label: "Default" },
                      ...filterOptions.groups.map((g) => ({ value: g, label: g })),
                    ]}
                    placeholder="All Groups"
                    key={`group-${data.loadedOrganisation}-${data.loadedProject}-${data.selectedGroup ?? "none"}`}
                  />
                  <FiltersWrapper.Select
                    name="classification"
                    label="Classification"
                    defaultValue={data.selectedClassification ?? undefined}
                    items={[
                      { value: "default", label: "Default" },
                      ...currentClassifications.map((c) => ({ value: c, label: c })),
                    ]}
                    placeholder={
                      data.selectedGroup == null
                        ? "Select Group First"
                        : data.selectedGroup === "default"
                          ? "N/A for Default group"
                          : "Select Classification"
                    }
                    disabled={data.selectedGroup == null || data.selectedGroup === "default"}
                    key={`classification-${data.selectedGroup ?? "none"}-${data.selectedClassification ?? "none"}`}
                  />
                </>
              )}
            </FiltersWrapper>
            <div className="flex-1" />
            <div className="flex shrink-0 items-center gap-3">
              <VectorThresholdsAddNew data={data} />
              <VectorThresholdsDeleteAll data={data} />
            </div>
          </div>
        )}
        <Outlet />
      </Fragment>
    )
  } catch {
    return (
      <div className="flex size-full flex-col gap-y-4 overflow-x-auto px-20 py-5">
        <h1 className="text-2xl font-bold tracking-tight lg:text-3xl">Vector Thresholds</h1>
        <div className="flex items-center justify-center py-8 text-gray-500">
          Error loading component. Please refresh the page.
        </div>
      </div>
    )
  }
}

const ThresholdRow = memo(
  ({
    threshold,
    index,
    data,
  }: {
    threshold: VectorThresholdItem
    index: number
    data: ReturnType<typeof useVectorThresholdData>
  }) => {
    return (
      <TableRow className="text-sm">
        <TableCell>{index + 1}.</TableCell>
        <TableCell>{threshold.brand}</TableCell>
        <TableCell>{threshold.usage}</TableCell>
        <TableCell>
          {threshold.classification != null && threshold.classification !== "" ? threshold.classification : "Default"}
        </TableCell>
        <TableCell>{threshold.threshold}</TableCell>
        <TableCell className="space-x-2">
          <VectorThresholdEdit data={data} threshold={threshold} />
          <VectorThresholdDelete data={data} threshold={threshold} />
        </TableCell>
      </TableRow>
    )
  },
)

ThresholdRow.displayName = "ThresholdRow"

function Main() {
  const rawData = useLoaderData<typeof loader>()

  const data = useMemo(() => {
    if (rawData == null) return null

    const classificationOptions = rawData.classifications.map(
      (classification: DocumentClassificationType) => classification.name,
    )

    return {
      ...rawData,
      classificationOptions,
      hasClassifications: rawData.classifications.length > 0,
    }
  }, [rawData])

  const tableContent = useMemo(() => {
    if (data == null) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="py-8 text-center text-gray-500">
            Error loading data. Please refresh the page.
          </TableCell>
        </TableRow>
      )
    }

    if (data.hasError) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="py-8 text-center text-gray-500">
            Error loading thresholds. Please try different filter selections or add a new threshold.
          </TableCell>
        </TableRow>
      )
    }

    if (data.thresholds == null || data.thresholds.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="py-8 text-center text-gray-500">
            No thresholds found for the selected filters.
          </TableCell>
        </TableRow>
      )
    }

    return data.thresholds.map((threshold: VectorThresholdItem, index: number) => (
      <ThresholdRow
        key={`threshold-${threshold.brand}-${threshold.usage}-${threshold.classification ?? "default"}-${index}`}
        threshold={threshold}
        index={index}
        data={data}
      />
    ))
  }, [data])

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <span className="sr-only">No.</span>
          </TableHead>
          <TableHead className="text-left">Brand</TableHead>
          <TableHead className="text-left">Usage</TableHead>
          <TableHead className="text-left">Classification</TableHead>
          <TableHead className="text-left">Threshold</TableHead>
          <TableHead>
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{tableContent}</TableBody>
    </Table>
  )
}

type VectorThresholdData = ReturnType<typeof useVectorThresholdData>

const VectorThresholdsAddNew = memo(({ data }: { data: VectorThresholdData }) => {
  const [open, setOpen] = useState(false)

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <PlusIcon /> Add Threshold
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Vector Threshold</DialogTitle>
        </DialogHeader>
        <VectorThresholdsAddNewForm data={data} closeDialog={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  )
})

VectorThresholdsAddNew.displayName = "VectorThresholdsAddNew"

function VectorThresholdsAddNewForm({ data, closeDialog }: { data: VectorThresholdData; closeDialog: () => void }) {
  const fetcher = useFetcher<typeof action>()
  const [createAnother, setCreateAnother] = useState(false)
  const [formKey, setFormKey] = useState<string>()
  const [selectedGroup, setSelectedGroup] = useState<string>("")
  const [selectedClassification, setSelectedClassification] = useState<string>("")

  const groupsWithClassifications = useMemo(() => {
    if (data?.classifications == null) return {}

    return data.classifications.reduce(
      (acc: Record<string, string[]>, doc: DocumentClassificationType) => {
        acc[doc.name] = doc.subClassifications.map((sub) => sub.name)
        return acc
      },
      {} as Record<string, string[]>,
    )
  }, [data?.classifications])

  const availableGroups = useMemo(() => {
    if (data?.classifications == null) return []
    return data.classifications.map((doc: DocumentClassificationType) => doc.name)
  }, [data?.classifications])

  const availableClassifications = useMemo(() => {
    if (selectedGroup === "") return []
    return groupsWithClassifications[selectedGroup] ?? []
  }, [selectedGroup, groupsWithClassifications])

  useEffect(() => {
    if (fetcher.state !== "loading" || fetcher.data?.action !== "CREATE" || fetcher.data?.errorMessage != null) return

    if (createAnother) {
      setFormKey(Math.random().toString(36))
      setSelectedGroup("")
      setSelectedClassification("")
    } else {
      closeDialog()
    }
  }, [fetcher.state, fetcher.data, createAnother, closeDialog])

  const handleGroupChange = useCallback((value: string) => {
    setSelectedGroup(value)
    setSelectedClassification("")
  }, [])

  const handleClassificationChange = useCallback((value: string) => {
    setSelectedClassification(value)
  }, [])

  if (data == null) return null

  return (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="CREATE" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation ?? ""} />
      <input type="hidden" name="project" value={data.loadedProject ?? ""} />

      <div>
        <label className="text-sm font-medium">Usage</label>
        <Input
          key={formKey}
          required
          type="text"
          name="usage"
          placeholder="Enter usage"
          className="w-full"
          maxLength={20}
        />
        <p className="mt-1 text-xs text-gray-500">Maximum 20 characters</p>
      </div>

      <div>
        <label className="text-sm font-medium">Group</label>
        <Select key={`group-${formKey ?? ""}`} name="group" value={selectedGroup} onValueChange={handleGroupChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Group (Optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Default (No group)</SelectItem>
            {availableGroups.map((group: string) => (
              <SelectItem key={group} value={group}>
                {group}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium">Classification</label>
        <Select
          key={`classification-${formKey ?? ""}-${selectedGroup}`}
          name="classification"
          value={selectedClassification}
          onValueChange={handleClassificationChange}
          disabled={selectedGroup === "" || selectedGroup === "none"}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Classification (Optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Default (No classification)</SelectItem>
            {selectedGroup === "" || selectedGroup === "none"
              ? data.hasClassifications &&
                data.classifications.map((classification: DocumentClassificationType, index: number) => (
                  <SelectItem key={`all-classification-${classification.name}-${index}`} value={classification.name}>
                    {classification.name}
                  </SelectItem>
                ))
              : availableClassifications.map((classification: string) => (
                  <SelectItem key={classification} value={classification}>
                    {classification}
                  </SelectItem>
                ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-sm font-medium">Threshold (0.01–1)</label>
        <Input
          key={formKey}
          required
          type="number"
          name="threshold"
          placeholder="Enter threshold value (0.01–1)"
          min="0.01"
          max="1"
          step="0.01"
          className="w-full"
        />
        <p className="mt-1 text-xs text-gray-500">Must be greater than 0 and at most 1 (e.g., 0.75 for 75%)</p>
      </div>

      <div className="flex items-center space-x-2 border-t pt-2">
        <input
          type="checkbox"
          id="create-another"
          checked={createAnother}
          onChange={(e) => setCreateAnother(e.target.checked)}
          className="rounded border border-input"
        />
        <label htmlFor="create-another" className="text-sm leading-none font-medium">
          Create another?
        </label>
      </div>

      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> Creating...
          </span>
        ) : (
          fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit">Create Threshold</Button>
      </div>
    </fetcher.Form>
  )
}

const VectorThresholdsDeleteAll = memo(({ data }: { data: VectorThresholdData }) => {
  const fetcher = useFetcher<typeof action>()

  if (data == null || !hasPermission("deleteAccess")) return null
  if (fetcher.data != null && fetcher.data.action !== "DELETE_ALL") return null

  return (
    <Popover key={fetcher.data?.key}>
      <PopoverTrigger asChild>
        <Button variant="destructive">
          <TrashIcon /> Delete All
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end">
        <p className="text-sm text-pretty">
          Are you sure you want to <span className="font-semibold">DELETE ALL</span> matching Vector Thresholds in the
          &quot;
          <span className="font-semibold">{data.loadedOrganisation}</span>&quot; organisation?
        </p>
        <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
          <input type="hidden" name="action" value="DELETE_ALL" />
          <input type="hidden" name="project" value={data.loadedProject} />
          <input type="hidden" name="organisation" value={data.loadedOrganisation} />
          <input type="hidden" name="filterUsage" value={data.activeUsage} />
          <input type="hidden" name="filterUsageCriteria" value={data.activeUsageCriteria} />
          <input type="hidden" name="filterGroup" value={data.selectedGroup ?? ""} />
          <input type="hidden" name="filterClassification" value={data.selectedClassification ?? ""} />
          {fetcher.state !== "idle" && (
            <span className="flex items-center justify-start gap-1 pr-4">
              <LoaderCircleIcon className="size-4 animate-spin" /> Deleting...
            </span>
          )}
          <div className="flex-1" />
          <PopoverClose asChild>
            <Button>Cancel</Button>
          </PopoverClose>
          <Button type="submit" variant="ghost">
            Confirm
          </Button>
        </fetcher.Form>
      </PopoverContent>
    </Popover>
  )
})

VectorThresholdsDeleteAll.displayName = "VectorThresholdsDeleteAll"

const VectorThresholdEdit = memo(
  ({ data, threshold }: { data: VectorThresholdData; threshold: VectorThresholdItem }) => {
    const [isOpen, setIsOpen] = useState(false)

    if (!hasPermission("writeAccess")) return null

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="icon" aria-label="Edit Threshold">
            <PencilIcon />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Threshold</DialogTitle>
          </DialogHeader>
          <VectorThresholdEditForm data={data} threshold={threshold} onSuccess={() => setIsOpen(false)} />
        </DialogContent>
      </Dialog>
    )
  },
)

VectorThresholdEdit.displayName = "VectorThresholdEdit"

function VectorThresholdEditForm({
  data,
  threshold,
  onSuccess,
}: {
  data: VectorThresholdData
  threshold: VectorThresholdItem
  onSuccess: () => void
}) {
  const fetcher = useFetcher<typeof mainAction>()
  const [removeClassification, setRemoveClassification] = useState(false)

  let group = ""
  if (threshold.classification != null && threshold.classification !== "") {
    group =
      data?.classifications.find((c: DocumentClassificationType) =>
        c.subClassifications.some((sub) => sub.name === threshold.classification),
      )?.name ?? ""
  }

  const [selectedGroup, setSelectedGroup] = useState<string>(group)
  const [selectedClassification, setSelectedClassification] = useState<string>(threshold.classification ?? "none")

  const groupsWithClassifications = useMemo(() => {
    if (data?.classifications == null) return {}

    return data.classifications.reduce(
      (acc: Record<string, string[]>, doc: DocumentClassificationType) => {
        acc[doc.name] = doc.subClassifications.map((sub) => sub.name)
        return acc
      },
      {} as Record<string, string[]>,
    )
  }, [data?.classifications])

  const availableGroups = useMemo(() => {
    if (data?.classifications == null) return []
    return data.classifications.map((doc: DocumentClassificationType) => doc.name)
  }, [data?.classifications])

  const availableClassifications = useMemo(() => {
    if (selectedGroup === "") return []
    return groupsWithClassifications[selectedGroup] ?? []
  }, [selectedGroup, groupsWithClassifications])

  const handleGroupChange = useCallback((value: string) => {
    setSelectedGroup(value)
    setSelectedClassification("none")
  }, [])

  const handleClassificationChange = useCallback((value: string) => {
    setSelectedClassification(value)
  }, [])

  useEffect(() => {
    if (fetcher.state !== "loading" || fetcher.data?.errorMessage != null) return
    onSuccess()
  }, [fetcher.state, fetcher.data, onSuccess])

  if (data == null) return null

  return (
    <div className="space-y-4">
      <fetcher.Form method="PATCH" className="space-y-4">
        <input type="hidden" name="action" value="EDIT" />
        <input type="hidden" name="organisation" value={data.loadedOrganisation} />
        <input type="hidden" name="project" value={data.loadedProject} />
        <input type="hidden" name="originalUsage" value={threshold.usage} />
        <input type="hidden" name="originalClassification" value={threshold.classification ?? ""} />
        <input type="hidden" name="removeClassification" value={removeClassification.toString()} />

        <div>
          <label className="text-sm font-medium">Usage</label>
          <Input
            required
            type="text"
            name="newUsage"
            defaultValue={threshold.usage}
            className="w-full"
            autoFocus
            maxLength={20}
          />
          <p className="mt-1 text-xs text-gray-500">Maximum 20 characters</p>
        </div>

        <div>
          <label className="text-sm font-medium">Group</label>
          <Select name="group" value={selectedGroup} onValueChange={handleGroupChange} disabled={removeClassification}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Group (Optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Default (No group)</SelectItem>
              {availableGroups.map((group: string) => (
                <SelectItem key={group} value={group}>
                  {group}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-medium">Classification</label>
          <Select
            name="newClassification"
            value={selectedClassification}
            onValueChange={handleClassificationChange}
            disabled={removeClassification || selectedGroup === "" || selectedGroup === "none"}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Classification" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Default (No classification)</SelectItem>
              {selectedGroup === "" || selectedGroup === "none"
                ? data.hasClassifications &&
                  data.classifications.map((classification: DocumentClassificationType, index: number) => (
                    <SelectItem
                      key={`edit-all-classification-${classification.name}-${index}`}
                      value={classification.name}
                    >
                      {classification.name}
                    </SelectItem>
                  ))
                : availableClassifications.map((classification: string) => (
                    <SelectItem key={`edit-group-classification-${classification}`} value={classification}>
                      {classification}
                    </SelectItem>
                  ))}
            </SelectContent>
          </Select>
        </div>
        {threshold.classification != null && threshold.classification !== "" && (
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="remove-classification"
              checked={removeClassification}
              onChange={(e) => setRemoveClassification(e.target.checked)}
              className="rounded border border-input"
            />
            <label htmlFor="remove-classification" className="text-sm leading-none font-medium">
              Remove classification entirely
            </label>
          </div>
        )}

        <div>
          <label className="text-sm font-medium">Threshold (0.01–1)</label>
          <Input
            required
            type="number"
            name="newThreshold"
            defaultValue={threshold.threshold}
            min="0.01"
            max="1"
            step="0.01"
            className="w-full"
          />
          <p className="mt-1 text-xs text-gray-500">Must be greater than 0 and at most 1</p>
        </div>

        <div className="flex items-center justify-start gap-4">
          {fetcher.state !== "idle" ? (
            <span className="flex items-center justify-start gap-1 pr-4">
              <LoaderCircleIcon className="size-4 animate-spin" /> Updating...
            </span>
          ) : (
            fetcher.data?.errorMessage != null && (
              <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
            )
          )}
          <div className="flex-1" />
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
          <Button type="submit">Update Threshold</Button>
        </div>
      </fetcher.Form>
    </div>
  )
}

const VectorThresholdDelete = memo(
  ({ data, threshold }: { data: VectorThresholdData; threshold: VectorThresholdItem }) => {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="icon" aria-label="Delete Threshold">
            <TrashIcon className="text-red-500" />
          </Button>
        </PopoverTrigger>
        <PopoverContent side="top" align="end" className="max-w-sm">
          <p className="text-sm text-pretty">
            Delete threshold for usage <span className="font-semibold">&quot;{threshold.usage}&quot;</span>
            {threshold.classification != null && threshold.classification !== "" && (
              <>
                {" "}
                with classification <span className="font-semibold">&quot;{threshold.classification}&quot;</span>
              </>
            )}
            ?
          </p>
          <VectorThresholdDeleteForm data={data} threshold={threshold} />
        </PopoverContent>
      </Popover>
    )
  },
)

VectorThresholdDelete.displayName = "VectorThresholdDelete"

function VectorThresholdDeleteForm({ data, threshold }: { data: VectorThresholdData; threshold: VectorThresholdItem }) {
  const fetcher = useFetcher<typeof mainAction>()

  if (data == null) return null

  return (
    <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
      <input type="hidden" name="action" value="DELETE" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="usage" value={threshold.usage} />
      <input type="hidden" name="classification" value={threshold.classification ?? ""} />

      {fetcher.state !== "idle" ? (
        <span className="flex items-center justify-start gap-1 pr-4">
          <LoaderCircleIcon className="size-4 animate-spin" /> Deleting...
        </span>
      ) : (
        fetcher.data?.action === "DELETE" &&
        fetcher.data.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
      )}
      <div className="flex-1" />
      <PopoverClose asChild>
        <Button>Cancel</Button>
      </PopoverClose>
      <Button type="submit" variant="ghost">
        Confirm
      </Button>
    </fetcher.Form>
  )
}

VectorThresholds.loader = loader
VectorThresholds.action = action
VectorThresholds.Main = Main
VectorThresholds.routeId = routeId

Main.action = mainAction
